import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Header } from '../components/Header';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Calendar, Clock, User, X, Coins, ArrowRight, Infinity } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MyBookings() {
  const { user, token, dbUser } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  // Check if user has unlimited access
  const hasUnlimited = dbUser?.unlimited_until && new Date(dbUser.unlimited_until) > new Date();



  const fetchBookings = useCallback(async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/bookings/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const sorted = response.data.sort((a, b) =>
        new Date(b.date) - new Date(a.date)
      );
      setBookings(sorted);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      toast.error('Erreur lors du chargement des réservations');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchBookings();
  }, [user, navigate, fetchBookings]);

  const handleCancel = async (bookingId) => {
    try {
      await axios.put(
        `${API}/bookings/${bookingId}/cancel`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Réservation annulée et crédits remboursés');
      fetchBookings();
    } catch (error) {
      console.error('Error cancelling booking:', error);
      toast.error('Erreur lors de l\'annulation');
    }
  };

  const getStatusBadge = (booking) => {
    if (booking.status === 'cancelled') {
      return <Badge variant="destructive">Annulée</Badge>;
    }
    if (booking.payment_status === 'paid') {
      return <Badge className="bg-green-500 hover:bg-green-600">Confirmée</Badge>;
    }
    return <Badge variant="secondary">En attente</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-24 pb-16">
          <div className="max-w-4xl mx-auto px-4">
            <Skeleton className="h-10 w-48 mb-8" />
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-32" />
              ))}
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <h1 className="font-heading font-black text-4xl mb-2">Mes Réservations</h1>
            <p className="text-muted-foreground">
              Gérez vos séances d'entraînement
            </p>
          </div>

          {/* Credits Card */}
          {dbUser && (
            <Card className="mb-6 border-2 border-[#D4FF00]/30" data-testid="my-credits-card">
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {hasUnlimited ? (
                      <Infinity className="w-6 h-6 text-[#D4FF00]" />
                    ) : (
                      <Coins className="w-6 h-6 text-[#D4FF00]" />
                    )}
                    <div>
                      <p className="text-sm text-muted-foreground">Vos crédits</p>
                      {hasUnlimited ? (
                        <p className="font-bold">Accès Illimité</p>
                      ) : (
                        <p className="font-bold">{dbUser.credits?.toFixed(1) || '0'} heures</p>
                      )}
                    </div>
                  </div>
                  <Link to="/credits">
                    <Button size="sm" variant="outline">
                      Acheter des crédits
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          )}

          {bookings.length === 0 ? (
            <Card className="text-center py-12" data-testid="no-bookings">
              <CardContent>
                <Calendar className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="font-heading font-bold text-xl mb-2">Aucune réservation</h3>
                <p className="text-muted-foreground mb-6">
                  Vous n'avez pas encore de réservation. Découvrez nos coachs et réservez votre première séance !
                </p>
                <Button
                  onClick={() => navigate('/trainers')}
                  className="bg-[#D4FF00] text-black hover:bg-[#bfe600] font-bold uppercase tracking-wide"
                  data-testid="browse-trainers-btn"
                >
                  Voir les coachs
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4" data-testid="bookings-list">
              {bookings.map((booking) => (
                <Card
                  key={booking.id}
                  className={`${booking.status === 'cancelled' ? 'opacity-60' : ''}`}
                  data-testid={`booking-${booking.id}`}
                >
                  <CardContent className="p-6">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3">
                          {getStatusBadge(booking)}
                          <Badge variant="outline" className="font-mono">
                            <Coins className="w-3 h-3 mr-1" />
                            {booking.amount} crédits
                          </Badge>
                        </div>

                        <h3 className="font-heading font-bold text-lg mb-2">
                          Séance avec {booking.trainer_name}
                        </h3>

                        <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4" />
                            {format(parseISO(booking.date), 'EEEE d MMMM yyyy', { locale: fr })}
                          </div>
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4" />
                            {booking.start_time} - {booking.end_time}
                          </div>
                        </div>
                      </div>

                      <div className="flex gap-2">
                        {booking.status !== 'cancelled' && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="outline"
                                className="text-destructive hover:text-destructive"
                                data-testid={`cancel-booking-${booking.id}`}
                              >
                                <X className="w-4 h-4 mr-2" />
                                Annuler
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Annuler la réservation ?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Cette action est irréversible. Le créneau sera de nouveau disponible pour d'autres utilisateurs.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Non, garder</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleCancel(booking.id)}
                                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                >
                                  Oui, annuler
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
